import React, { useState } from 'react';
import { X, Trash2, Plus, Minus, ShoppingBag, MessageCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';

const formatPrice = (n) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CartDrawer = ({ open, onClose, items, onUpdateQty, onRemove, onClear }) => {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');

  const total = items.reduce((sum, it) => sum + it.price * it.qty, 0);

  const buildOrderText = () => {
    const lines = [];
    lines.push('Bună ziua,');
    lines.push('');
    lines.push('Doresc să comand următoarele produse:');
    lines.push('');
    items.forEach((it, idx) => {
      lines.push(`${idx + 1}. ${it.name}`);
      lines.push(`   Cod produs: ${it.code}`);
      lines.push(`   Cantitate: ${it.qty}`);
      lines.push(`   Preț: ${formatPrice(it.price)} lei`);
      lines.push(`   Subtotal: ${formatPrice(it.price * it.qty)} lei`);
      lines.push('');
    });
    lines.push('-----------------------------------');
    lines.push(`TOTAL COMANDĂ: ${formatPrice(total)} lei`);
    lines.push('-----------------------------------');
    lines.push('');
    lines.push('Date de contact:');
    lines.push(`Nume: ${name}`);
    lines.push(`Adresa de livrare: ${address}`);
    lines.push('');
    lines.push('Vă mulțumesc!');
    return lines.join('\n');
  };

  const handleConfirmEmail = () => {
    if (items.length === 0) return;
    const subject = 'Comandă nouă - EddMa Metal Art';
    const body = buildOrderText();
    const to = 'eddma.metal.art@gmail.com';
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, '_blank', 'noopener,noreferrer');

    toast.success('Se deschide Gmail într-un tab nou', {
      description: 'Verifică detaliile și apasă Send pentru a finaliza comanda.'
    });

    setTimeout(() => {
      onClear();
      setName('');
      setAddress('');
      onClose();
    }, 1000);
  };

  const handleConfirmWhatsApp = () => {
    if (items.length === 0) return;
    const text = buildOrderText();
    // Romanian phone +40 747 825 465
    const phoneNumber = '40747825465';
    const waUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(text)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');

    toast.success('Se deschide WhatsApp...', {
      description: 'Apasă Send pentru a trimite comanda.'
    });

    setTimeout(() => {
      onClear();
      setName('');
      setAddress('');
      onClose();
    }, 1000);
  };

  const formValid = name.trim() && address.trim();

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[440px] bg-white z-[70] shadow-2xl transform transition-transform duration-300 flex flex-col ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-[#1a1a1a] text-white">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            Coșul meu ({items.length})
          </h2>
          <button onClick={onClose} className="p-1 hover:text-amber-400 transition-colors" aria-label="Închide">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8 text-gray-500">
              <ShoppingBag className="w-16 h-16 mb-4 text-gray-300" />
              <p className="font-medium text-gray-700 mb-1">Coșul tău este gol</p>
              <p className="text-sm">Adaugă produse pentru a finaliza o comandă.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {items.map((it) => (
                <li key={it.id} className="flex gap-3 p-4">
                  <img
                    src={it.image}
                    alt={it.name}
                    className="w-20 h-20 object-cover rounded-sm bg-gray-100 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm text-gray-800 line-clamp-2 leading-snug mb-1">{it.name}</h4>
                    <div className="text-[11px] text-gray-500 mb-2">Cod: {it.code}</div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center border border-gray-200 rounded-sm">
                        <button
                          onClick={() => onUpdateQty(it.id, Math.max(1, it.qty - 1))}
                          className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 transition-colors"
                          aria-label="Scade cantitatea"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="w-8 text-center text-sm font-medium">{it.qty}</span>
                        <button
                          onClick={() => onUpdateQty(it.id, it.qty + 1)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 transition-colors"
                          aria-label="Crește cantitatea"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="font-bold text-gray-900 text-sm">{formatPrice(it.price * it.qty)} lei</span>
                    </div>
                  </div>
                  <button
                    onClick={() => onRemove(it.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors h-fit"
                    aria-label="Șterge"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-gray-200 p-5 space-y-3 bg-gray-50">
            <input
              type="text"
              required
              placeholder="Numele tău *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-amber-500 bg-white"
            />
            <textarea
              required
              placeholder="Adresa de livrare * (oraș, stradă, număr, județ)"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-amber-500 bg-white resize-none"
            />
            <div className="flex items-baseline justify-between pt-2">
              <span className="text-sm text-gray-600">Total</span>
              <span className="text-2xl font-bold text-gray-900">{formatPrice(total)} lei</span>
            </div>

            <button
              type="button"
              onClick={handleConfirmWhatsApp}
              disabled={!formValid}
              className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold uppercase text-sm tracking-wide py-3 rounded-sm transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <MessageCircle className="w-4 h-4" />
              Comandă pe WhatsApp
            </button>

            <button
              type="button"
              onClick={handleConfirmEmail}
              disabled={!formValid}
              className="w-full flex items-center justify-center gap-2 bg-[#1a1a1a] hover:bg-amber-500 text-white font-semibold uppercase text-sm tracking-wide py-3 rounded-sm transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <Mail className="w-4 h-4" />
              Comandă pe Email
            </button>

            {!formValid && (
              <p className="text-xs text-gray-500 text-center">
                Completează numele și adresa pentru a confirma comanda
              </p>
            )}
            <button
              type="button"
              onClick={onClear}
              className="w-full text-xs text-gray-500 hover:text-red-500 transition-colors py-1"
            >
              Golește coșul
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
